import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  getUserPermissions,
  canAccessProject,
  invalidatePermissionsCache,
  type PermissionScope,
} from '../lib/permissions';

interface UsePermissionsResult {
  permissions: Map<string, PermissionScope>;
  loading: boolean;
  can: (permissionKey: string) => boolean;
  scope: (permissionKey: string) => PermissionScope;
  canInProject: (projectId: string, permissionKey: string) => Promise<boolean>;
  refresh: () => void;
}

export function usePermissions(): UsePermissionsResult {
  const { profile } = useAuth();
  const [permissions, setPermissions] = useState<Map<string, PermissionScope>>(new Map());
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!profile) {
      setPermissions(new Map());
      setLoading(false);
      return;
    }

    try {
      const perms = await getUserPermissions(profile.role);
      setPermissions(perms);
    } catch {
      setPermissions(new Map());
    } finally {
      setLoading(false);
    }
  }, [profile?.id, profile?.role]);

  useEffect(() => {
    load();
  }, [load]);

  const can = useCallback(
    (permissionKey: string): boolean => {
      if (!profile) return false;
      if (profile.role === 'Admin') return true;
      const s = permissions.get(permissionKey);
      return !!s && s !== 'none';
    },
    [profile, permissions]
  );

  const scope = useCallback(
    (permissionKey: string): PermissionScope => {
      if (!profile) return 'none';
      if (profile.role === 'Admin') return 'all';
      return permissions.get(permissionKey) || 'none';
    },
    [profile, permissions]
  );

  const canInProject = useCallback(
    async (projectId: string, permissionKey: string): Promise<boolean> => {
      if (!profile) return false;
      return canAccessProject(projectId, permissionKey, profile.role, profile.id);
    },
    [profile?.id, profile?.role]
  );

  const refresh = useCallback(() => {
    invalidatePermissionsCache();
    load();
  }, [load]);

  return { permissions, loading, can, scope, canInProject, refresh };
}
